import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Utils, authJsonHeaders } from '../../utils';
import './payment.css';

const PlanChangeRequest = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [message, setMessage] = useState('');

    const plan = location.state?.plan || null;

    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('userData') || 'null');
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (!userData?.id) {
            navigate('/signIn?redirect=' + encodeURIComponent('/plan-change-request'));
        }
    }, [userData, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await fetch(`${Utils.baseurl}user/plan-change-request`, {
                method: 'POST',
                headers: authJsonHeaders(),
                body: JSON.stringify({
                    message: message.trim() || undefined,
                    origin: window.location.origin,
                }),
            });
            const result = await res.json();

            if (!result.status) {
                toast.error(result.message || 'Could not submit request');
                return;
            }

            setSubmitted(true);
            toast.success(result.message || 'Request submitted');
        } catch (err) {
            console.error(err);
            toast.error('Could not submit request');
        } finally {
            setSubmitting(false);
        }
    };

    if (!userData?.id) {
        return null;
    }

    if (submitted) {
        return (
            <div className="payment-page">
                <div className="payment-success-container">
                    <div className="success-animation">
                        <svg className="checkmark" viewBox="0 0 52 52">
                            <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                            <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                        </svg>
                    </div>
                    <h2>Request submitted</h2>
                    <p>Our team will review your plan change request and contact you shortly.</p>
                    <button type="button" className="pay-button" onClick={() => navigate('/apps')}>
                        Back to dashboard
                    </button>
                </div>
            </div>
        );
    }

    const planLabel = plan?.name || 'Enterprise';
    const displayName = userData.first_name
        ? `${userData.first_name} ${userData.last_name || ''}`.trim()
        : (userData.name || userData.email);

    return (
        <div className="payment-page">
            <div className="payment-container" style={{ gridTemplateColumns: '1fr', maxWidth: '640px' }}>
                <div className="payment-form-section">
                    <button type="button" className="back-button" onClick={() => navigate('/plan')}>
                        ← Back to plans
                    </button>
                    <h1>Request {planLabel}</h1>
                    <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                        Enterprise plans use custom pricing. Submit a request and our team will review your account and get in touch.
                    </p>

                    <div style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        marginBottom: '1.5rem',
                        fontSize: '14px',
                        color: '#334155',
                    }}>
                        <div><strong>Account:</strong> {displayName}</div>
                        <div><strong>Email:</strong> {userData.email}</div>
                        <p style={{ margin: '12px 0 0', color: '#64748b', fontSize: '13px' }}>
                            Your company and profile details from signup will be shared with our team — no need to enter them again.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label htmlFor="message">Anything else we should know? (optional)</label>
                            <textarea
                                id="message"
                                className="form-control"
                                rows={4}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Expected volume, channels, timeline…"
                            />
                        </div>

                        <button type="submit" className="pay-button" disabled={submitting}>
                            {submitting ? 'Submitting…' : 'Submit request'}
                        </button>
                    </form>

                    <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
                        New to notifySphere?{' '}
                        <button type="button" className="link-button" onClick={() => navigate('/signup')}>
                            Request full account access
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PlanChangeRequest;
